import React, { useState } from 'react';
import { Box, Text, Inline, xcss, Pressable } from '@atlaskit/primitives';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import Comment from '@atlassian/jira-issue-view-activity-comment/src/comment-view/index.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { createFullIssueUrl } from '@atlassian/jira-issue-view-store/src/selectors/breadcrumbs-selector.tsx';
import { messages } from './messages.tsx';

type PinnedCommentsType = {
	pinnedCommentsIds: string[];
};
export const PinnedComments = ({ pinnedCommentsIds }: PinnedCommentsType) => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const issueKey = useIssueKey();

	const onClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: isOpen ? 'collapsed' : 'expanded',
				actionSubject: 'pinnedComments',
			}),
		);
		setIsOpen(!isOpen);
	};

	const fullIssueUrl = createFullIssueUrl('', issueKey);

	if (pinnedCommentsIds?.length === 0) {
		return null;
	}

	return (
		<Box xcss={pinnedCommentsWrapperStyles}>
			<Pressable xcss={pinnedCommentHeaderStyles} onClick={onClick}>
				<Inline>
					<Box xcss={pinnedCommentHeaderTextStyles}>
						<Text>{formatMessage(messages.pinnedCommentsHeader)}</Text>
					</Box>
					{isOpen ? (
						<ChevronDownIcon
							spacing="spacious"
							label={formatMessage(messages.expandPinnedComments)}
							aria-label={formatMessage(messages.expandPinnedComments)}
							size="small"
						/>
					) : (
						<ChevronRightIcon
							spacing="spacious"
							label={formatMessage(messages.collapsePinnedComments)}
							aria-label={formatMessage(messages.collapsePinnedComments)}
							size="small"
						/>
					)}
				</Inline>
			</Pressable>
			{isOpen && (
				<Box xcss={pinnedCommentsContentStyles}>
					{pinnedCommentsIds.map((commentId) => {
						return (
							<Comment
								key={commentId}
								commentId={commentId}
								fullIssueUrl={fullIssueUrl}
								canEdit={false}
								canCreateWorkItemInComment={false}
								isEditing={false}
								isSaving={false}
								isError={false}
								isOptimistic={false}
								canScrollToOriginalCommentFromPinned
							/>
						);
					})}
				</Box>
			)}
			<Box xcss={horizontalDividerStyles} />
		</Box>
	);
};

const pinnedCommentsWrapperStyles = xcss({
	marginTop: 'space.300',
	marginBottom: 'space.150',
});

const pinnedCommentHeaderStyles = xcss({
	font: token('font.heading.xxsmall'),
	color: 'color.text.subtle',
	fontWeight: 'font.weight.medium',
	backgroundColor: 'color.background.neutral.subtle',
});

const pinnedCommentsContentStyles = xcss({
	paddingTop: 'space.050',
	paddingBottom: 'space.050',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'border.radius.100',
});

const horizontalDividerStyles = xcss({
	height: '1px',
	width: '100%',
	backgroundColor: 'color.background.accent.gray.subtler',
	marginTop: 'space.200',
});

const pinnedCommentHeaderTextStyles = xcss({
	marginTop: 'space.025',
});
